import { Loader2, Megaphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * Themed "campaign building" progress indicator for Google Ads campaign
 * creation — a gently rocking megaphone broadcasting staggered pulse waves.
 * Sibling of the app's other themed loaders (content gen = writing pencil,
 * Technical Audit = FixingLoader hammer). Coral theme tokens; honors
 * prefers-reduced-motion (megaphone holds still, waves stop pulsing).
 *
 * Two variants:
 *  - "panel" (default) — a centered block for Overview/progress areas.
 *  - "inline" — compact row (e.g. replacing the Approve/Reject buttons while
 *    the approve mutation is in flight).
 *
 * Plus a sibling <CampaignBuildingOverlay/> — a full-page fixed overlay
 * (cousin of FixingOverlay, but page-level rather than contained) with a
 * large megaphone and a "Continue in background" escape hatch.
 */

const CAMPAIGN_LOADER_KEYFRAMES = `
@keyframes cbl-megaphone-rock {
  0%, 100% { transform: rotate(-8deg); }
  50% { transform: rotate(8deg); }
}
@keyframes cbl-broadcast-wave {
  0% { opacity: 0; transform: translateY(-50%) rotate(45deg) scale(0.55); }
  30% { opacity: 0.85; }
  100% { opacity: 0; transform: translateY(-50%) rotate(45deg) scale(1.2); }
}
.cbl-megaphone {
  transform-origin: 35% 70%;
  animation: cbl-megaphone-rock 1.6s ease-in-out infinite;
}
.cbl-wave {
  position: absolute;
  left: 100%;
  top: 42%;
  transform: translateY(-50%) rotate(45deg);
  border: 2px solid var(--color-coral);
  border-left-color: transparent;
  border-bottom-color: transparent;
  border-radius: 9999px;
  opacity: 0;
  pointer-events: none;
  animation: cbl-broadcast-wave 1.7s ease-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .cbl-megaphone { animation: none; }
  .cbl-wave { animation: none; opacity: 0.45; }
}
`;

interface CampaignBuildingLoaderProps {
  /** Stage label, e.g. "Researching keywords…". */
  stage?: string;
  /** Optional secondary line under the stage label (panel variant only). */
  subtext?: string;
  /** "panel" (default): centered block. "inline": compact row. */
  variant?: 'panel' | 'inline';
  className?: string;
}

function MegaphoneWithWaves({
  iconClassName,
  waveSizes,
}: {
  iconClassName: string;
  waveSizes: number[];
}) {
  return (
    <span className="relative inline-flex">
      <Megaphone
        className={cn('cbl-megaphone text-coral', iconClassName)}
        aria-hidden="true"
      />
      {waveSizes.map((size, idx) => (
        <span
          key={size}
          className="cbl-wave"
          style={{
            width: size,
            height: size,
            marginLeft: -size / 2,
            animationDelay: `${idx * 0.35}s`,
          }}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

export function CampaignBuildingLoader({
  stage = 'Building the campaign…',
  subtext,
  variant = 'panel',
  className,
}: CampaignBuildingLoaderProps) {
  if (variant === 'inline') {
    return (
      <span
        className={cn('inline-flex items-center gap-2.5', className)}
        role="status"
        aria-live="polite"
      >
        <style>{CAMPAIGN_LOADER_KEYFRAMES}</style>
        <span className="mr-2 inline-flex">
          <MegaphoneWithWaves iconClassName="h-5 w-5" waveSizes={[10, 16]} />
        </span>
        <span className="text-sm font-medium text-coral-dark">{stage}</span>
      </span>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-coral/20 bg-coral-light/30 px-6 py-8 text-center',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <style>{CAMPAIGN_LOADER_KEYFRAMES}</style>
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-coral-light shadow-sm">
        <MegaphoneWithWaves iconClassName="h-8 w-8" waveSizes={[14, 22, 30]} />
      </span>
      <div className="space-y-1">
        <div className="text-sm font-medium text-coral-dark">{stage}</div>
        {subtext ? (
          <div className="text-xs text-muted-foreground">{subtext}</div>
        ) : null}
      </div>
    </div>
  );
}

interface CampaignBuildingOverlayProps {
  /** Stage label, e.g. "Researching keywords…". */
  stage?: string;
  /** Secondary line under the stage label. */
  subtext?: string;
  /**
   * "Continue in background" handler — the caller hides the overlay and
   * falls back to its inline loader. Omitting it hides the button.
   */
  onDismiss?: () => void;
  /**
   * "Cancel run" handler — stops the in-progress build. Omitting it hides the
   * button (e.g. during setup before a run id exists, a run can't be cancelled
   * yet). Rendered as a destructive-outline action next to "Continue in
   * background".
   */
  onCancel?: () => void;
  /** Disables the cancel button and shows a spinner while the cancel is in flight. */
  cancelPending?: boolean;
  className?: string;
}

/**
 * Full-page campaign-building overlay: fixed, above dialogs (z-[60] vs the
 * app's z-50 overlays), warm blurred backdrop, large megaphone + waves.
 * Callers mount/unmount it based on run state — it renders whenever mounted.
 */
export function CampaignBuildingOverlay({
  stage = 'Building the campaign…',
  subtext,
  onDismiss,
  onCancel,
  cancelPending = false,
  className,
}: CampaignBuildingOverlayProps) {
  return (
    <div
      className={cn(
        'fixed inset-0 z-[60] flex flex-col items-center justify-center gap-5',
        'bg-background/80 backdrop-blur-sm',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <style>{CAMPAIGN_LOADER_KEYFRAMES}</style>
      <span className="flex h-28 w-28 items-center justify-center rounded-full bg-coral-light shadow-md">
        <MegaphoneWithWaves iconClassName="h-14 w-14" waveSizes={[22, 34, 46]} />
      </span>
      <div className="max-w-md space-y-1.5 px-6 text-center">
        <div className="text-lg font-semibold text-coral-dark">{stage}</div>
        {subtext ? (
          <div className="text-sm text-muted-foreground">{subtext}</div>
        ) : null}
      </div>
      {onDismiss || onCancel ? (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onDismiss ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDismiss}
              className="bg-card/70"
            >
              Continue in background
            </Button>
          ) : null}
          {onCancel ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={cancelPending}
              className="border-destructive/40 bg-card/70 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              {cancelPending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Cancelling…
                </>
              ) : (
                'Cancel run'
              )}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
